import * as autobind from 'autobind';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Input, Icon, Message, Grid } from 'semantic-ui-react';

import { MachineUnitDto, MachineUnitsStore } from '../../../../../stores/configuration/machinery/machine-units-store';
import { ItemState, OrderDefinition, Query } from '../../../../../stores/dataStore';
import { CommandResult } from '../../../../../stores/types';
import { nameof } from '../../../../../utils/object';
import EquipmentTypeEditor from '../../../../../widgets/bussiness/equipment-type-editor';
import OemEditor from '../../../../../widgets/bussiness/oem-editor';
import { TableModel, TableView } from '../../../../../widgets/collections/table';
import { TextBoxFilter } from '../../../../../widgets/collections/table-filters/textbox-filter';
import NewMachineUnitView from './new-machine-unit';
import ChangeMachineUnitView from './edit-machine-unit';

export interface MachineUnitsListProps extends WithTranslation, RouteComponentProps {
  machineUnits: MachineUnitsStore;
  readOnly: boolean;
}

export interface MachineUnitsListState {
  query: Query;
  newMachineUnitShown: boolean;
  changeMachineUnitShown: boolean;
  activeFilters: string[];
  selectedItem: any;
  someFilterOpened: boolean;
}

@connect(['machineUnits', MachineUnitsStore])
class MachineUnitsListPage extends React.Component<MachineUnitsListProps, MachineUnitsListState> {
  handleOnEnterKeydown = item => {
    !this.props.readOnly && this.setState({ selectedItem: item }, () => this.onEditItem());
  };
  handleOnRowDoubleClick = item => {
    this.handleOnEnterKeydown(item);
  };
  constructor(props) {
    super(props);
    this.state = {
      query: {
        searchQuery: '',
        orderBy: [{ direction: 'Ascending', field: nameof<MachineUnitDto>('name'), useProfile: false }],
        skip: 0,
        take: 10
      },
      newMachineUnitShown: false,
      changeMachineUnitShown: false,
      activeFilters: [],
      selectedItem: null,
      someFilterOpened: false
    };
  }

  componentDidMount() {
    this.load();
  }

  @autobind
  private load() {
    this.props.machineUnits.getAllAsync(this.state.query);
  }

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState(
      {
        query: Object.assign(this.state.query, {
          orderBy: [...orderBy, { direction: 'Descending', field: 'modifiedOn', useProfile: false }]
        })
      },
      this.load
    );
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) });
    this.load();
  }

  @autobind
  private async onSaveRow(item: MachineUnitDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.machineUnits.saveAsync(item, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private async onDelete(item: MachineUnitDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.machineUnits.deleteAsync(item.id, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private handleFilterChange(filters: { id: string; filter: any }[]) {
    const filter = filters.map(f => f.filter);
    const activeFilters = filters.map(f => f.id);

    const query = Object.assign(this.state.query, { filter, skip: 0 });
    this.setState({ query, activeFilters }, () => this.load());
  }

  @autobind
  private onNewItem() {
    this.setState({ newMachineUnitShown: true });
  }

  @autobind
  private onNewItemClosed(isSuccess: boolean) {
    this.setState({ newMachineUnitShown: false });
    if (isSuccess) this.load();
  }

  @autobind
  private onEditItem() {
    this.setState({ changeMachineUnitShown: true });
  }

  @autobind
  private onEditItemClosed(isSuccess: boolean) {
    this.setState({ changeMachineUnitShown: false });
    if (isSuccess) this.load();
  }

  handleOnActivateFilter = (visible: boolean) => {
    this.setState({ someFilterOpened: visible });
  };

  public render() {
    const { t } = this.props as any;
    const { activeFilters, someFilterOpened } = this.state;

    const tableModel = {
      columns: [
        {
          title: t('Cluster'),
          renderer: data => <>{data.clusterName}</>,
          tooltipRenderer: true,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by Cluster')}
              triggerTitle={title}
              onFilter={value =>
                onFilter(nameof<MachineUnitDto>('clusterName'), `contains(tolower(EquipmentType/Cluster/Name), '${value.toLowerCase()}')`)
              }
              onClear={() => onClear(nameof<MachineUnitDto>('clusterName'))}
              active={activeFilters.includes(nameof<MachineUnitDto>('clusterName'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: 'EquipmentType/Cluster/Name',
            useProfile: false
          }
        },
        {
          title: t('Equipment Type'),
          tooltipRenderer: true,
          renderer: data => data.equipmentTypeName,
          editor: (data, onChange) => (
            <EquipmentTypeEditor
              nullable
              value={{ id: data.equipmentTypeId, title: data.equipmentTypeName }}
              onChange={c => {
                data.equipmentTypeId = c == null ? null : c.id;
                data.equipmentTypeName = c == null ? '' : c.title;
                onChange();
              }}
            />
          ),
          //<Input value={data.clusterName} fluid onChange={(e, { value }) => { data.clusterName = value; onChange(); }} />,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by Equipment Type')}
              triggerTitle={title}
              onFilter={value =>
                onFilter(nameof<MachineUnitDto>('equipmentTypeName'), `contains(tolower(EquipmentType/Name), '${value.toLowerCase()}')`)
              }
              onClear={() => onClear(nameof<MachineUnitDto>('equipmentTypeName'))}
              active={activeFilters.includes(nameof<MachineUnitDto>('equipmentTypeName'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: 'EquipmentType/Name',
            useProfile: false
          }
        },
        {
          title: t('OEM'),
          tooltipRenderer: true,
          renderer: data => data.oemName,
          editor: (data, onChange) => (
            <OemEditor
              nullable
              value={{ id: data.oemId, title: data.oemName }}
              onChange={c => {
                data.oemId = c == null ? null : c.id;
                data.oemName = c == null ? '' : c.title;
                onChange();
              }}
            />
          ),
          //<Input value={data.clusterName} fluid onChange={(e, { value }) => { data.clusterName = value; onChange(); }} />,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by OEM')}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<MachineUnitDto>('oemName'), `contains(tolower(Oem/Name), '${value.toLowerCase()}')`)}
              onClear={() => onClear(nameof<MachineUnitDto>('oemName'))}
              active={activeFilters.includes(nameof<MachineUnitDto>('oemName'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: 'Oem/Name',
            useProfile: false
          }
        },
        {
          title: t('Machine Unit'),
          tooltipRenderer: true,
          renderer: data => data.name,
          editor: (data, onChange) => (
            <Input
              value={data.name}
              fluid
              onChange={(e, { value }) => {
                data.name = value;
                onChange();
              }}
            />
          ),
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by Machine Unit')}
              triggerTitle={title}
              onFilter={value =>
                onFilter(nameof<MachineUnitDto>('name'), `contains(tolower(${nameof<MachineUnitDto>('name')}), '${value.toLowerCase()}')`)
              }
              onClear={() => onClear(nameof<MachineUnitDto>('name'))}
              active={activeFilters.includes(nameof<MachineUnitDto>('name'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: nameof<MachineUnitDto>('name'),
            useProfile: false,
            active: 'Ascending'
          }
        }
      ],
      data: this.props.machineUnits.state
    } as TableModel<MachineUnitDto>;
    return (
      <>
        <Grid className="event-types-list-grid">
          {this.props.readOnly && <h2>{t('Machine Units')}</h2>}
          {this.props.machineUnits.state.result && !this.props.machineUnits.state.result.isSuccess && (
            <Grid.Row className="event-types-list-error-row">
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.props.machineUnits.state.result.messages.map(o => o.body)}
              />
            </Grid.Row>
          )}
          <Grid.Row className="event-types-list-items-row request-list__table-view">
            <TableView
              /////////////////For build table keyboard navegation/////////////////
              selectable={!this.state.newMachineUnitShown && !this.state.changeMachineUnitShown && !this.props.readOnly}
              maxSelection={1}
              onHideCheckbox={true}
              selectionType={'allRow'}
              onEnterKeydown={this.handleOnEnterKeydown}
              onRowDoubleClick={this.handleOnRowDoubleClick}
              preventEnterKeyDownEvent={someFilterOpened}
              //showActionsConfirmModal={true}
              /////////////////For build table keyboard navegation/////////////////
              model={tableModel}
              extraActions={
                !this.props.readOnly
                  ? [
                      {
                        content: (
                          <>
                            <Icon name="edit" />
                            &nbsp;{t('Edit')}
                          </>
                        ),
                        onClick: item => {
                          this.setState({ selectedItem: item }, () => this.onEditItem());
                        }
                      }
                    ]
                  : []
              }
              onOrderByChanged={this.handleOrderBy}
              onNewItem={this.onNewItem}
              onRefresh={this.load}
              canEdit={false}
              canDelete={!this.props.readOnly}
              onDeleteRow={this.onDelete}
              onSaveRow={this.onSaveRow}
              onPageChange={this.handlePageChange}
              onFilterChange={this.handleFilterChange}
              canCreateNew={!this.props.readOnly}
              createNewButtonTitle={t('Add Machine Unit')}
            ></TableView>
          </Grid.Row>
        </Grid>
        {(this.state.newMachineUnitShown && <NewMachineUnitView onClose={this.onNewItemClosed} {...this.props} />) ||
          (this.state.changeMachineUnitShown && (
            <ChangeMachineUnitView onClose={this.onEditItemClosed} currentMU={this.state.selectedItem} />
          ))}
      </>
    );
  }
}

export default withTranslation()(MachineUnitsListPage);
